/**
 * Tiktok 发品 / 编辑确认前预检：
 * 1) Tiktok 发品约束 GET .../publish/standard（非 Shein fill-in-standard）
 * 2) 币种须落在 CNY/USD（或站点列表，若自运营已接入）
 * 3) 商家 SKU 本地必填与长度
 *
 * Shein 专属接口（关联属性规则、supplierSku 平台查重、fill_in_standard_list）不在 Tiktok 路径调用。
 */

import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import { tiktokPlatformAPI } from '@/utils/api';
import {
  parseFillInStandardMap,
  resolveSkuPackageCapabilities,
} from '@/tiktok-platform/utils/tiktokPublishFillInStandard';
import { validateShopPublishCurrency } from '@/tiktok-platform/utils/resolveTiktokPublishCurrency';
import { tiktokShopCapabilities } from '@/tiktok-platform/utils/tiktokShopCapabilities';
import { extractSiteListCurrencies } from '@/tiktok-platform/utils/tiktokSiteList';
import { TIKTOK_SELLER_SKU_MAX_LEN } from '@/tiktok-platform/utils/tiktokPublishFormLimits';

export { parseFillInStandardMap, TIKTOK_SELLER_SKU_MAX_LEN };

/**
 * @deprecated Temu 货号短横线预留规则；TTS Create Product 的 seller_sku 无此要求。
 * 保留导出以免旧调用方崩溃，新逻辑请用 validateSupplierSkusLocal。
 */
export function hasReservedOrFilledHyphenCode(value) {
  const raw = String(value ?? '').trim();
  if (!raw || !raw.includes('-')) return false;
  if (raw.endsWith('-')) return raw.replace(/-+$/, '').length > 0;
  const parts = raw.split('-').filter(Boolean);
  return parts.length >= 2;
}

function fail(message, section = 'basic', field = 'form') {
  return { ok: false, message: fixedUi(message), section, field };
}

function unwrapInfo(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  if (raw.info != null) return raw.info;
  return raw;
}

function isTiktokPublishStandard(raw) {
  const info = unwrapInfo(raw) || {};
  return info.tiktokPublishStandard === true
    || String(info.source || '').toLowerCase() === 'tiktok';
}

function collectSupplierSkuRows(form = {}) {
  const rows = [];
  (form.skcList || []).forEach((skc, skcIndex) => {
    (skc?.skuRows || []).forEach((sku, skuIndex) => {
      const supplierSku = String(sku?.supplierSku || '').trim();
      if (!supplierSku) return;
      const originalSupplierSku = String(
        sku?.originalSupplierSku || sku?.original_supplier_sku || '',
      ).trim();
      rows.push({
        supplierSku,
        originalSupplierSku,
        skuCode: String(sku?.skuCode || sku?.platformSku || '').trim(),
        skcIndex,
        skuIndex,
        colorLabel: skc?.colorLabel || skc?.colorExtraValue || `规格${skcIndex + 1}`,
        sizeLabel: sku?.sizeLabel || sku?.sizeValueId || `SKU${skuIndex + 1}`,
      });
    });
  });
  return rows;
}

/**
 * 本地：商家 SKU（Create Product seller_sku）必填。
 * 官方：Valid length 1–50；Text without spaces。不再要求 Temu 短横线预留。
 */
export function validateSupplierSkusLocal(form = {}) {
  for (const skc of form.skcList || []) {
    for (const sku of skc?.skuRows || []) {
      const color = skc?.colorLabel || skc?.colorExtraValue || fixedUi('规格');
      const size = sku?.sizeLabel || sku?.sizeValueId || 'SKU';
      const supplierSku = String(sku?.supplierSku || '').trim();
      if (!supplierSku) {
        return fail(
          fixedUiTpl('请填写「{0} / {1}」商家 SKU（seller_sku）', color, size),
          'spec',
          'supply',
        );
      }
      if (/\s/.test(supplierSku)) {
        return fail(
          fixedUiTpl(
            '「{0} / {1}」商家 SKU 不能包含空格（Create Product：Text without spaces）',
            color,
            size,
          ),
          'spec',
          'supply',
        );
      }
      if (supplierSku.length > TIKTOK_SELLER_SKU_MAX_LEN) {
        return fail(
          fixedUiTpl(
            '「{0} / {1}」商家 SKU 不能超过 {2} 个字符',
            color,
            size,
            TIKTOK_SELLER_SKU_MAX_LEN,
          ),
          'spec',
          'supply',
        );
      }
    }
  }
  const rows = collectSupplierSkuRows(form);
  if (!rows.length) {
    return fail('请为每个 SKU 填写商家 SKU（seller_sku）', 'spec', 'supply');
  }
  return { ok: true };
}

/**
 * Tiktok 发品约束校验（非 Shein fill_in_standard_list）。
 */
function validateAgainstTiktokStandard(form, standardRaw, attrTpl) {
  const info = unwrapInfo(standardRaw) || {};
  const maxLen = Number(
    info.titleMaxLength
    ?? info.productNameMaxLength
    ?? info.default_language_title_max_length
    ?? 0,
  );
  const title = String(form.title || form.productName || '');
  if (maxLen > 0 && title.length > maxLen) {
    return fail(
      fixedUiTpl('商品名称超过上限（{0} 字符，当前 {1}）', maxLen, title.length),
      'basic',
      'title',
    );
  }
  const titleEn = String(form.titleEn || form.englishTitle || '');
  if (maxLen > 0 && titleEn.length > maxLen) {
    return fail(
      fixedUiTpl('英文名称超过上限（{0} 字符，当前 {1}）', maxLen, titleEn.length),
      'basic',
      'titleEn',
    );
  }

  // Tiktok 不走 Shein quantity_info / package_type；保持安全默认不强制件数
  const caps = resolveSkuPackageCapabilities(standardRaw);
  void attrTpl;
  return {
    ok: true,
    standardMap: parseFillInStandardMap(standardRaw),
    standardInfo: info,
    skuPkgCaps: caps,
  };
}

/**
 * @param {object} opts
 * @param {string|number} opts.shopId
 * @param {object} opts.form
 * @param {boolean} [opts.isEdit]
 * @param {object|null} [opts.attrTpl]
 * @param {string} [opts.shopType]
 * @param {object|null} [opts.shopCaps]
 * @param {object|null} [opts.siteList]
 */
export async function runTiktokPublishPreflight({
  shopId,
  form,
  isEdit = false,
  attrTpl = null,
  shopType = '',
  shopCaps = null,
  siteList = null,
}) {
  const localSku = validateSupplierSkusLocal(form);
  if (!localSku.ok) return localSku;

  const categoryId = Number(form.categoryId);
  let standardRaw = null;
  try {
    standardRaw = await tiktokPlatformAPI.getPublishFillInStandard(shopId, {
      catId: Number.isFinite(categoryId) ? categoryId : undefined,
      category_id: Number.isFinite(categoryId) ? categoryId : undefined,
    });
  } catch (ex) {
    return fail(ex?.message || '查询 Tiktok 发品规范失败，请稍后重试', 'basic', 'form');
  }

  if (!isTiktokPublishStandard(standardRaw) && standardRaw != null) {
    // 兼容异常回包：仍按 Tiktok 路径宽松校验，不按 Shein field_key 拦截
  }
  const std = validateAgainstTiktokStandard(form, standardRaw, attrTpl);
  if (!std.ok) return std;

  const caps = shopCaps || tiktokShopCapabilities(shopType || form?.shopType);
  let resolvedSiteList = siteList || form?.siteList || null;
  if (caps.usesPriceInfo && !extractSiteListCurrencies(resolvedSiteList).length && shopId) {
    try {
      const siteRes = await tiktokPlatformAPI.getShopSiteList(shopId, { refresh: true });
      resolvedSiteList = siteRes?.siteList ?? resolvedSiteList;
    } catch {
      // 自运营站点列表未接入时，币种校验会回退到 publish/standard
    }
  }
  const currencyCheck = validateShopPublishCurrency(form, {
    shopCaps: caps,
    shopType: shopType || form?.shopType,
    siteList: resolvedSiteList,
    standardRaw,
  });
  if (!currencyCheck.ok) {
    // 自运营站点未接入且规范有币种时，上面已回退；此处仅真实失败才拦
    return currencyCheck;
  }

  // Tiktok 暂无 Shein 关联属性规则 / supplierSku 平台查重；不调用对应 stub，避免误伤
  void isEdit;
  return {
    ok: true,
    standardRaw,
    standardMap: std.standardMap,
    standardInfo: std.standardInfo,
    skuPkgCaps: std.skuPkgCaps,
  };
}
